import { type FormEvent, type JSX, useCallback, useEffect, useState } from 'react';
import { AbTablet } from '../komponenten/AbTablet';

import {
  absagen,
  anlegen,
  ARTNAME,
  belegen,
  kalender as ladeKalender,
  KREISE,
  liste as ladeListe,
  standorte as ladeStandorte,
  statusname,
  ZUGEHOERIGKEITSNAME,
  type Belegungszeile,
  type Kreis,
  type Ressourcenart,
  type Ressourcenzeile,
} from '../api/ressourcen';
import { ApiFehler } from '../api/fehler';
import { alsTag as tagImHausformat } from '../datum';
import { markenklasse, type Markenart } from '../marken';
import { Meldung } from '../komponenten/Meldung';
import { Liste } from '../komponenten/Liste';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';
import { Modulkonfiguration } from './Modulkonfiguration';
import { Ressourcenkonfiguration } from './Ressourcenkonfiguration';

/**
 * Ressourcen: Stamm und Belegungsübersicht (RES-01, RES-17, RES-18).
 *
 * ============================================================================
 * EINE MASKE, ZWEI KREISE
 * ============================================================================
 *
 * Der Auftraggeber am 11.09.2026: „ressourcenmenü mit zwei punkten Assets und
 * Trainer". Beides sind Ressourcen — RES-01 verlangt ausdrücklich EIN Modell für
 * Personen, Räume, Geräte und Fahrzeuge —, aber zwei verschiedene
 * Arbeitsgegenstände: Am Fahrmodul pflegt man Bausteine und Platzzahlen, am
 * Trainer Qualifikationen, Verträge und Honorare.
 *
 * Deshalb zwei Menüpunkte und EINE Maske mit einem `kreis`. Zwei Masken wären
 * zweimal Stamm, zweimal Kalender, zweimal Zuordnung — und die zweite wäre die,
 * in der eine Erweiterung fehlt.
 *
 * Der Kreis ist kein Filter, den man wegklicken kann: Er entscheidet, was die
 * Sicht überhaupt zeigt, bis in die Anlage hinein. Der Artfilter darin wählt
 * innerhalb des Kreises.
 *
 * ============================================================================
 * ZWEI SICHTEN AUF DIESELBE SACHE
 * ============================================================================
 *
 * **Stamm** beantwortet „was haben wir?", **Kalender** beantwortet „was ist am
 * 14. März um 9 Uhr belegt?". Das sind zwei Fragen, und wer sie in eine Maske
 * zwingt, beantwortet keine gut: Die Stammliste braucht Filter nach Art und
 * Standort, der Kalender braucht einen Zeitraum.
 *
 * Sie stehen trotzdem in EINER Maske, weil sie dieselbe Auswahl teilen — wer
 * im Stamm nach Trainern filtert und dann in den Kalender wechselt, will dort
 * Trainer sehen.
 *
 * ============================================================================
 * DER KALENDER ZEIGT AUCH DIE ABGESAGTEN (RES-17)
 * ============================================================================
 *
 * Er ZEIGT, er entscheidet nicht. Eine abgesagte Zuordnung, die verschwindet,
 * nimmt dem Disponenten die Antwort auf „war da nicht mal jemand
 * eingeteilt?". Sie steht deshalb blass da statt gar nicht.
 *
 * ============================================================================
 * DIE ABWEISUNG NENNT DEN GRUND
 * ============================================================================
 *
 * Stößt sich eine Zuordnung, kommt vom Server nicht „belegt", sondern womit:
 * eine Sperrzeit mit ihrem Grund, eine Belegung mit ihrer Zahl, die Regelzeit.
 * Die Maske zeigt das unverändert — sie ist an dieser Stelle nur das Fenster
 * (ERR-01).
 *
 * ============================================================================
 * SEITENTYP LISTE (ÄA-41, Auftrag #19)
 * ============================================================================
 *
 * Über der Tabelle die Filterleiste (Sicht, Art, Standort, im Kalender Tag und
 * Zeitraum), der Zähler im Seitenkopf. Die Seitenleiste trägt, was neben der
 * Tabelle ausgefüllt wird: im Stamm die neue Ressource, sobald jemand sie
 * anlegt, im Kalender die Zuordnung. Ohne offene Anlage nimmt der Stamm die
 * volle Breite — neun Spalten brauchen sie mehr als ein leerer Kasten.
 *
 * DIE PFLEGE EINER RESSOURCE IST EINE AKTENANSICHT UND KEINE SEITENLEISTE.
 * Sie ersetzt die Liste, wie sie es vorher tat, und steht in Abschnitten mit
 * Feldraster (Ressourcenkonfiguration, Modulkonfiguration). Drei Gründe aus
 * dem, was die Maske heute tut:
 *
 *   * Sie ist zu breit für 320 px: Stammdaten, Eigenschaften, Sperrzeiten,
 *     Zugehörigkeit, Onlinezugang, Rahmenvereinbarung mit Absagestaffel und
 *     Honorar, Abrechnungsgrundlage — fünf Tabellen und sechs Formulare. Die
 *     Bausteine eines Moduls sind eine Tabelle mit vierzehn Zeilen.
 *   * Sie wird GEPFLEGT und nicht nur angesehen. Eine Vorschau, die beim
 *     Überfahren der nächsten Zeile wechselt, nähme einen halb ausgefüllten
 *     Stammdatenentwurf mit.
 *   * Ein Filter über einer Liste, die gerade nicht bearbeitet wird, wäre eine
 *     Bedienung ins Leere (AeA-05) — „Schließen" bzw. „Zurück zum Stamm"
 *     führt in die Liste mit demselben Filter zurück.
 */

/** Der Tag als `YYYY-MM-DD`, wie ein `<input type="date">` ihn führt. */
function alsTag(datum: Date): string {
  const versatz = datum.getTimezoneOffset() * 60_000;
  return new Date(datum.getTime() - versatz).toISOString().slice(0, 10);
}

/** Aus Tag und Uhrzeit ein ISO-Zeitpunkt in ORTSZEIT — nicht in UTC. */
function alsZeitpunkt(tag: string, uhrzeit: string): string {
  return new Date(`${tag}T${uhrzeit}:00`).toISOString();
}

function uhr(zeitpunkt: string): string {
  return new Date(zeitpunkt).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/*
 * DIE NAMEN SIND DIE DER STILDATEI UND NICHT DIE DER SACHE.
 *
 * Der erste Entwurf schrieb hier `achtung` und `fehler` — beides gibt es in
 * `stil.css` nicht; sie heissen `warn` und `krit`. Die Marke erschien deshalb
 * ohne Farbe, und das faellt bei einem Status, den niemand aendern konnte,
 * nicht auf. Seit die Konfiguration ihn aendert, faellt es auf.
 */
const STATUSMARKE: Readonly<Record<string, Markenart>> = {
  /* Eine Ressource im Dienst ist der Normalfall und kein Erfolg. */
  aktiv: 'laufend',
  wartung: 'aufmerksamkeit',
  ausser_betrieb: 'sackgasse',
  ausgemustert: 'stillgelegt',
};

/*
 * GEMESSEN AM 14.09.2026: `vorlaeufig` zeigte hier auf `.marke--achtung` —
 * eine Regel, die es nie gab. Die Marke blieb farblos, und niemand sah es;
 * derselbe Fehler wie am 04.09.2026 auf der Statusseite. Seit die Maske eine
 * ART nennt statt eines Klassennamens, ist ein Vertipper ein Typfehler.
 */
const BELEGUNGSMARKE: Readonly<Record<string, Markenart>> = {
  bestaetigt: 'abschluss',
  zugesagt: 'abschluss',
  vorlaeufig: 'aufmerksamkeit',
  abgesagt: 'stillgelegt',
  verfallen: 'stillgelegt',
};

export function Ressourcen({
  kreis,
  darfHonorar = false,
  darfHonorarAendern = false,
  darfEingangsrechnungPruefen = false,
  darfPflegen = false,
}: {
  /**
   * Welcher Kreis — `assets` oder `trainer`. Siehe den Kopf dieser Datei.
   *
   * OHNE VORGABEWERT, und das ist Absicht: Ein Vorgabewert hieße, dass ein
   * Aufrufer, der ihn vergisst, lautlos eine der beiden Sichten bekommt. Der
   * Menüpunkt entscheidet es, und er muss es sagen.
   */
  readonly kreis: Kreis;
  /**
   * RES-73: Die Ressourcenakte eines externen Trainers zeigt sein Honorar — aber
   * nur dem, der `artikel/honorar_sehen` trägt. Operations pflegt Ressourcen
   * und sieht keine Honorare (Tabelle 11).
   *
   * Vorgabewert `false`: Was Geld zeigt, zeigt es nur, wenn es ausdrücklich
   * erlaubt ist. Die Rechte werden hier nur DURCHGEREICHT — entschieden wird
   * serverseitig.
   */
  readonly darfHonorar?: boolean;
  readonly darfHonorarAendern?: boolean;
  /** RES-76: die Buchhaltung prüft Eingangsrechnungen gegen die Grundlage. */
  readonly darfEingangsrechnungPruefen?: boolean;
  /**
   * SYS-01, BEN-02 (Auftrag #32): eine Ressource anlegen — `ressource:pflegen`. Wer den Stamm
   * nur sieht (Empfang, Vertrieb, Service, Geschaeftsleitung), bekommt den
   * Knopf nicht; die API wiese ihn mit 403 ab.
   */
  readonly darfPflegen?: boolean;
}): JSX.Element {
  /** Die Arten dieses Kreises — die Obergrenze für alles in dieser Maske. */
  const arten: readonly Ressourcenart[] = KREISE[kreis];
  const [sicht, setzeSicht] = useState<'stamm' | 'kalender'>('stamm');
  /*
   * Welches Modul gerade konfiguriert wird. Die Bausteine eines Moduls sind
   * keine dritte Sicht auf den Stamm, sondern die Innenansicht EINER Zeile —
   * deshalb eine Auswahl und kein weiterer Wert in `sicht` (AeA-05).
   */
  const [konfiguriert, setzeKonfiguriert] = useState<Ressourcenzeile | null>(null);
  /*
   * Welche Ressource gerade EINGESTELLT wird (RES-01 bis RES-04, RES-14,
   * RES-15).
   *
   * Zwei Auswahlen und nicht eine mit Reiter: „Was läuft auf diesem Modul"
   * (AeA-05) und „wie ist diese Ressource eingestellt" sind verschiedene
   * Fragen an verschiedene Tabellen, und die eine gibt es nur für Module und
   * Räume. Ein gemeinsamer Zustand hieße, an jeder Stelle mitzuführen, welche
   * der beiden gemeint ist.
   */
  const [eingestellt, setzeEingestellt] = useState<Ressourcenzeile | null>(null);
  const [art, setzeArt] = useState<string>('');
  const [standort, setzeStandort] = useState('');

  /*
   * DIE STANDORTE ZUR AUSWAHL (13.09.2026).
   *
   * Auftraggeber: „Standort mit Auswahlmöglichkeit." Vorher standen hier zwei
   * freie Textfelder — im Filter und beim Anlegen —, und beide verlangten die
   * KENNUNG: Wer „Lüneburg" tippte, filterte ins Leere oder legte eine
   * Ressource an einem Standort an, den es nicht gibt (er heißt LUENEBURG).
   * Der Fehler fiel erst auf, wenn sie in keiner Planung auftauchte.
   */
  const [standorte, setzeStandorte] = useState<
    readonly { readonly kennung: string; readonly bezeichnung: string }[]
  >([]);

  const [ressourcen, setzeRessourcen] = useState<readonly Ressourcenzeile[]>([]);
  const [belegungen, setzeBelegungen] = useState<readonly Belegungszeile[]>([]);
  const [tag, setzeTag] = useState(() => alsTag(new Date()));
  const [tage, setzeTage] = useState(1);

  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [laedt, setzeLaedt] = useState(false);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  /** Die Zuordnung, deren Absage im Pop-up nachgefragt wird (CRM-104). */
  const [nachfrage, setzeNachfrage] = useState<Belegungszeile | null>(null);

  /* Die Zuordnung (RES-18). */
  const [zuRessource, setzeZuRessource] = useState('');
  const [vonUhr, setzeVonUhr] = useState('09:00');
  const [bisUhr, setzeBisUhr] = useState('13:00');
  const [zuStatus, setzeZuStatus] = useState<'vorlaeufig' | 'bestaetigt'>('vorlaeufig');
  const [zuPlaetze, setzeZuPlaetze] = useState('');
  const [zuBemerkung, setzeZuBemerkung] = useState('');

  /* Der Stamm (RES-01). */
  const [neuOffen, setzeNeuOffen] = useState(false);
  const [neuKennung, setzeNeuKennung] = useState('');
  const [neuBezeichnung, setzeNeuBezeichnung] = useState('');
  /*
   * Die erste Art des Kreises — „Fahrmodul" bei den Assets, „Trainer" bei den
   * Trainern. Sie steht in KREISE und nicht hier: Eine zweite Liste derselben
   * Reihenfolge liefe beim ersten Zuwachs auseinander.
   */
  const [neuArt, setzeNeuArt] = useState<Ressourcenart>(() => KREISE[kreis][0]);
  const [neuStandort, setzeNeuStandort] = useState('');
  const [neuBelegungsart, setzeNeuBelegungsart] =
    useState<'ausschliesslich' | 'plaetze'>('ausschliesslich');
  const [neuPlatzzahl, setzeNeuPlatzzahl] = useState('');
  /*
   * ÄA-21: Bei einer Person ist die Zugehörigkeit Pflicht — die Datenbank
   * verlangt sie (0181). Vorbelegt mit `extern`, weil dieses Haus heute
   * ausschließlich externe Trainer führt; wer einen Angestellten anlegt, stellt
   * um, und das ist ein Handgriff, den er bewusst tut.
   */
  const [neuZugehoerigkeit, setzeNeuZugehoerigkeit] = useState<'intern' | 'extern'>('extern');

  const holen = useCallback(async () => {
    setzeLaedt(true);
    try {
      const gefunden = await ladeListe({
        /*
         * KEIN FILTER HEISST: DER GANZE KREIS. Nicht „alles" — eine Trainerin in
         * der Assetliste wäre kein Treffer, sondern ein Fehler, und man sähe ihr
         * nicht an, dass sie dort nicht hingehört.
         */
        arten: art === '' ? arten : [art as Ressourcenart],
        ...(standort.trim() === '' ? {} : { standort: standort.trim() }),
      });
      setzeRessourcen(gefunden);
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, [art, arten, standort]);

  useEffect(() => {
    void holen();
  }, [holen]);

  /*
   * EINMAL GEHOLT UND NICHT JE FILTERWECHSEL: Standorte sind Stammdaten, sie
   * ändern sich selten. Ein Fehlschlag bleibt still — die Liste ist dann leer,
   * und die Auswahl fällt auf „alle Standorte" zurück. Eine Fehlermeldung über
   * den Ressourcen wegen einer Auswahlliste wäre lauter als die Sache.
   */
  useEffect(() => {
    void (async () => {
      try {
        setzeStandorte(await ladeStandorte());
      } catch {
        setzeStandorte([]);
      }
    })();
  }, []);

  const holeKalender = useCallback(async () => {
    setzeLaedt(true);
    try {
      const von = new Date(`${tag}T00:00:00`);
      const bis = new Date(von.getTime() + tage * 86_400_000);
      setzeBelegungen(
        await ladeKalender({
          von: von.toISOString(),
          bis: bis.toISOString(),
          arten: art === '' ? arten : [art as Ressourcenart],
          ...(standort.trim() === '' ? {} : { standort: standort.trim() }),
        }),
      );
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, [tag, tage, art, arten, standort]);

  useEffect(() => {
    if (sicht === 'kalender') void holeKalender();
  }, [sicht, holeKalender]);

  const zuordnen = useCallback(
    async (ereignis: FormEvent) => {
      ereignis.preventDefault();
      if (zuRessource === '') return;
      setzeLaedt(true);
      setzeFehler(null);
      setzeMeldung(null);
      try {
        await belegen({
          ressource: zuRessource,
          von: alsZeitpunkt(tag, vonUhr),
          bis: alsZeitpunkt(tag, bisUhr),
          status: zuStatus,
          ...(zuPlaetze.trim() === '' ? {} : { plaetze: Number(zuPlaetze) }),
          ...(zuBemerkung.trim() === '' ? {} : { bemerkung: zuBemerkung.trim() }),
        });
        setzeMeldung('Die Zuordnung ist angelegt.');
        if (sicht === 'kalender') await holeKalender();
      } catch (f) {
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      } finally {
        setzeLaedt(false);
      }
    },
    [zuRessource, tag, vonUhr, bisUhr, zuStatus, zuPlaetze, zuBemerkung, sicht, holeKalender],
  );

  /**
   * Die Zuordnung absagen (RES-35) — aus der Rückfrage im Pop-up (CRM-104).
   *
   * Sie WIRFT und fängt nicht: Ein Fehler erscheint im Pop-up, das dabei offen
   * bleibt, und nicht ein zweites Mal über der Liste. Erst der Erfolg schließt
   * es; ein Fehler beim Neuladen danach gehört wieder der Seite.
   */
  const aufheben = useCallback(
    async (belegung: string) => {
      setzeFehler(null);
      setzeMeldung(null);
      await absagen(belegung);
      setzeNachfrage(null);
      setzeMeldung('Die Zuordnung ist abgesagt. Sie bleibt im Kalender sichtbar.');
      await holeKalender();
    },
    [holeKalender],
  );

  const anlegenAbsenden = useCallback(
    async (ereignis: FormEvent) => {
      ereignis.preventDefault();
      setzeLaedt(true);
      setzeFehler(null);
      try {
        await anlegen({
          kennung: neuKennung.trim(),
          bezeichnung: neuBezeichnung.trim(),
          art: neuArt,
          belegungsart: neuBelegungsart,
          ...(neuArt === 'trainer' || neuArt === 'dienstleister'
            ? { zugehoerigkeit: neuZugehoerigkeit }
            : {}),
          ...(neuStandort.trim() === '' ? {} : { standort: neuStandort.trim() }),
          ...(neuBelegungsart === 'plaetze' && neuPlatzzahl.trim() !== ''
            ? { platzzahl: Number(neuPlatzzahl) }
            : {}),
        });
        setzeNeuOffen(false);
        setzeNeuKennung('');
        setzeNeuBezeichnung('');
        setzeNeuPlatzzahl('');
        setzeMeldung('Die Ressource ist angelegt.');
        await holen();
      } catch (f) {
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      } finally {
        setzeLaedt(false);
      }
    },
    [
      neuKennung,
      neuBezeichnung,
      neuArt,
      neuZugehoerigkeit,
      neuStandort,
      neuBelegungsart,
      neuPlatzzahl,
      holen,
    ],
  );

  /*
   * ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist, und nicht,
   * solange eine einzelne Ressource offen ist: Dann zählt die Liste nichts,
   * was zu sehen wäre.
   */
  useZaehler(
    laedt || konfiguriert !== null || eingestellt !== null
      ? undefined
      : sicht === 'stamm'
        ? zaehlwort(ressourcen.length, 'Ressource', 'Ressourcen')
        : zaehlwort(belegungen.length, 'Belegung', 'Belegungen'),
  );

  /*
   * Die Bausteine eines Moduls ERSETZEN die Liste, sie stehen nicht darunter.
   * Ein Filter ueber einer Liste, die gerade nicht zu sehen ist, waere eine
   * Bedienung ins Leere.
   */
  if (konfiguriert !== null) {
    return (
      <Modulkonfiguration
        modul={konfiguriert}
        beiSchliessen={() => {
          setzeKonfiguriert(null);
        }}
      />
    );
  }

  if (eingestellt !== null) {
    /* Auf dem Telefon nur lesen (Auftrag CRM-106, ÄA-63). */
    return (
      <AbTablet>
      <Ressourcenkonfiguration
        ressource={eingestellt}
        darfHonorar={darfHonorar}
        darfHonorarAendern={darfHonorarAendern}
        darfEingangsrechnungPruefen={darfEingangsrechnungPruefen}
        beiSchliessen={() => {
          setzeEingestellt(null);
        }}
        /*
         * Die Liste dahinter zeigt Kennung, Bezeichnung, Standort, Belegung
         * und Status. Wer eines davon ändert und dann schließt, sähe sonst
         * den alten Stand — und hielte die Änderung für verloren.
         */
        beiAenderung={() => {
          void holen();
        }}
      />
      </AbTablet>
    );
  }

  /* Die Filterleiste: Sicht, Art, Standort — im Kalender dazu Tag und Zeitraum. */
  const filter = (
    <>
      <select
        className="liste__filter"
        aria-label="Sicht"
        value={sicht}
        onChange={(e) => {
          setzeSicht(e.target.value as 'stamm' | 'kalender');
        }}
      >
        <option value="stamm">Stamm</option>
        <option value="kalender">Belegung</option>
      </select>

      <select
        className="liste__filter"
        aria-label="Nach Art filtern"
        value={art}
        onChange={(e) => {
          setzeArt(e.target.value);
        }}
      >
        <option value="">Alle Arten</option>
        {arten.map((a) => (
          <option key={a} value={a}>
            {ARTNAME[a]}
          </option>
        ))}
      </select>

      {/*
        EINE AUSWAHL UND KEIN TEXTFELD (13.09.2026).

        Der Filter verlangte die KENNUNG des Standorts, nicht seinen Namen —
        „Lüneburg" traf nichts, „LUENEBURG" traf alles. Ein Feld, in dem man
        raten muss, ist kein Filter.
      */}
      <select
        className="liste__filter"
        aria-label="Nach Standort filtern"
        value={standort}
        onChange={(e) => {
          setzeStandort(e.target.value);
        }}
      >
        <option value="">Alle Standorte</option>
        {standorte.map((o) => (
          <option key={o.kennung} value={o.kennung}>
            {o.bezeichnung}
          </option>
        ))}
      </select>

      {sicht === 'kalender' && (
        <>
          <input
            type="date"
            className="liste__filter"
            aria-label="Tag"
            value={tag}
            onChange={(e) => {
              setzeTag(e.target.value);
            }}
          />
          {/*
           * Tages-, Wochen- und Monatssicht sind derselbe Aufruf mit
           * anderem Zeitraum (RES-17) — die Sicht ist eine Frage der
           * Anzeige, nicht der Auskunft.
           */}
          <select
            className="liste__filter"
            aria-label="Zeitraum"
            value={String(tage)}
            onChange={(e) => {
              setzeTage(Number(e.target.value));
            }}
          >
            <option value="1">Tag</option>
            <option value="7">Woche</option>
            <option value="31">Monat</option>
          </select>
        </>
      )}

      {/*
        DIE HAUPTHANDLUNG DER LISTE. Sie öffnet die Anlage in der Seitenleiste
        und schließt sie wieder — derselbe Knopf, damit der Weg zurück dort
        liegt, wo der Weg hinein lag.
      */}
      {sicht === 'stamm' && darfPflegen && (
        <button
          type="button"
          className={neuOffen ? 'knopf' : 'knopf knopf--haupt'}
          onClick={() => {
            setzeNeuOffen(!neuOffen);
          }}
        >
          {neuOffen ? 'Abbrechen' : 'Ressource anlegen'}
        </button>
      )}
    </>
  );

  const hinweise =
    meldung === null ? undefined : (
      <p className="hinweiszeile" role="status">
        {meldung}
      </p>
    );

  return (
    <>
      {fehler !== null && <Meldung fehler={fehler} />}

      {sicht === 'stamm' && (
        <Liste
          filter={filter}
          hinweise={hinweise}
          vorschauBeschriftung="Neue Ressource"
          {...(neuOffen ? { vorschau: anlageformular() } : {})}
        >
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle">
              <caption className="nur-vorlesen">Ressourcenstamm</caption>
              <thead>
                <tr>
                  <th scope="col">Kennung</th>
                  <th scope="col">Bezeichnung</th>
                  <th scope="col">Art</th>
                  {/*
                    ÄA-21 und RES-39: intern oder extern, und ob die Vereinbarung
                    steht. Beides in der LISTE — ein externer Trainer ohne
                    Vereinbarung sieht das Tagesbild nicht (ÄA-20), und wer dafür
                    46 Ressourcen einzeln öffnen müsste, fände es nie.
                  */}
                  <th scope="col">Zugehörigkeit</th>
                  <th scope="col">Standort</th>
                  {/* „Belegung raus, da immer nur einzeln" — beim Trainer stünde
                      46-mal dasselbe Wort (13.09.2026). */}
                  {kreis !== 'trainer' && <th scope="col">Belegung</th>}
                  <th scope="col">Status</th>
                  <th scope="col">Bausteine</th>
                  <th scope="col">Handlung</th>
                </tr>
              </thead>
              <tbody>
                {ressourcen.map((r) => (
                  <tr key={r.id}>
                    <td className="liste__nummer">{r.kennung}</td>
                    <td className="liste__name">{r.bezeichnung}</td>
                    <td>{ARTNAME[r.art]}</td>
                    <td>
                      {r.zugehoerigkeit === null ? (
                        '—'
                      ) : (
                        <>
                          {ZUGEHOERIGKEITSNAME[r.zugehoerigkeit] ?? r.zugehoerigkeit}
                          {r.auftragsverarbeitung === 'fehlt' && (
                            <>
                              {' '}
                              <span className="marke marke--warn">Vereinbarung fehlt</span>
                            </>
                          )}
                          {r.auftragsverarbeitung === 'gekuendigt' && (
                            <>
                              {' '}
                              <span className="marke marke--warn">gekündigt</span>
                            </>
                          )}
                        </>
                      )}
                    </td>
                    <td>{r.standort ?? '—'}</td>
                    {kreis !== 'trainer' && (
                      <td>
                        {r.belegungsart === 'plaetze'
                          ? `${String(r.platzzahl ?? 0)} Plätze`
                          : 'ausschließlich'}
                      </td>
                    )}
                    <td>
                      <span className={markenklasse(STATUSMARKE[r.status] ?? 'stillgelegt')}>
                        {statusname(r.status, r.art)}
                      </span>
                    </td>
                    <td>
                      {/*
                       * Nur Module und Raeume tragen Bausteine (AeA-05). Was
                       * ein Trainer kann, steht als Qualifikation; die
                       * Datenbank weist alles andere ab, und ein Knopf, der
                       * nur in eine Abweisung fuehrt, gehoert nicht hin.
                       *
                       * DIE ZAHL STEHT SCHON HIER. Ein vergessener Eintrag
                       * macht ein Modul fuer einen Baustein unsichtbar; wer
                       * dafuer elf Module einzeln oeffnen muss, bemerkt es
                       * nie. "0 von 14" traegt deshalb die Marke fuer den
                       * ungepflegten Stand und faellt in der Spalte auf.
                       *
                       * Beide Faelle stehen in einer Marke gleicher Breite,
                       * damit Zahl und Knopf in jeder Zeile buendig sitzen
                       * (CRM-72); nur die Farbe unterscheidet sie.
                       */}
                      {(r.art === 'modul' || r.art === 'raum') && (
                        <span className="bausteinzelle">
                          <span
                            className={
                              r.bausteineHinterlegt === 0
                                ? 'marke marke--inaktiv bausteinzelle__zahl'
                                : 'marke bausteinzelle__zahl'
                            }
                          >
                            {`${String(r.bausteineHinterlegt)} von ${String(r.bausteineGesamt)}`}
                          </span>
                          <button
                            type="button"
                            className="knopf knopf--klein"
                            onClick={() => {
                              setzeKonfiguriert(r);
                            }}
                          >
                            Bausteine
                          </button>
                        </span>
                      )}
                    </td>
                    <td>
                      {/*
                        DIE EINSTELLUNG GIBT ES FUER JEDE ART (RES-01).
                        Was auf einem Modul laeuft, betrifft nur Module und
                        Raeume — wie eine Ressource eingestellt ist, betrifft
                        jede: der Trainer seine Qualifikationen (RES-02), das
                        Fahrzeug seine Wartungsfenster (RES-15), der Raum seine
                        Platzzahl (RES-52).
                      */}
                      <button
                        type="button"
                        className="knopf knopf--klein"
                        onClick={() => {
                          setzeEingestellt(r);
                        }}
                      >
                        Einstellen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {ressourcen.length === 0 && !laedt && (
            <div className="liste__leer" role="status">
              Keine Ressource zu diesem Filter.
            </div>
          )}
        </Liste>
      )}

      {sicht === 'kalender' && (
        <Liste
          filter={filter}
          hinweise={hinweise}
          vorschauBeschriftung="Neue Zuordnung"
          vorschau={zuordnungsformular()}
        >
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle">
              <caption className="nur-vorlesen">Belegungen im gewählten Zeitraum</caption>
              <thead>
                <tr>
                  <th scope="col">Ressource</th>
                  <th scope="col">Von</th>
                  <th scope="col">Bis</th>
                  <th scope="col">Status</th>
                  <th scope="col">Anlass</th>
                  <th scope="col">
                    <span className="nur-vorlesen">Handlung</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {belegungen.map((b) => (
                  <tr key={b.id} className={b.hart ? undefined : 'liste__zeile--blass'}>
                    <td>
                      <span className="liste__name liste__nummer">{b.ressourcenkennung}</span>
                      <div className="hilfe">{b.ressourcenbezeichnung}</div>
                    </td>
                    <td className="liste__einzeilig">
                      {tagImHausformat(b.von)} {uhr(b.von)}
                    </td>
                    <td className="liste__einzeilig">{uhr(b.bis)}</td>
                    <td>
                      <span className={markenklasse(BELEGUNGSMARKE[b.status] ?? 'stillgelegt')}>
                        {b.status}
                      </span>
                      {/* RES-22: Die Frist einer vorläufigen Zuordnung steht in der Achtungsfarbe. */}
                      {b.status === 'vorlaeufig' && b.verfaelltAm !== null && (
                        <div className="hilfe">
                          <span className="marke marke--warn">
                            verfällt {new Date(b.verfaelltAm).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      )}
                    </td>
                    <td>
                      {b.terminbezeichnung ?? b.bemerkung ?? '—'}
                      {b.plaetze !== null && <div className="hilfe">{b.plaetze} Plätze</div>}
                    </td>
                    <td>
                      {b.hart && (
                        <button
                          type="button"
                          className="knopf knopf--eingriff knopf--klein"
                          disabled={laedt}
                          onClick={() => {
                            setzeNachfrage(b);
                          }}
                        >
                          Absagen …
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {belegungen.length === 0 && !laedt && (
            <div className="liste__leer" role="status">
              Keine Belegung in diesem Zeitraum.
            </div>
          )}
        </Liste>
      )}

      {/*
        * DIE ABSAGE FRAGT NACH (CRM-104).
        *
        * Abgesagt heißt nicht gelöscht (RES-35): Die Zeile bleibt im Kalender
        * stehen, aber ein Weg zurück fehlt — wer sich vergriffen hat, legt die
        * Zuordnung neu an, und eine angefragte Person wird dabei neu gefragt.
        */}
      {nachfrage !== null && (
        <Rueckfrage
          frage="Zuordnung absagen?"
          unterzeile={`${nachfrage.ressourcenkennung} · ${tagImHausformat(nachfrage.von) ?? ''} ${uhr(nachfrage.von)}–${uhr(nachfrage.bis)}`}
          tat="Absagen"
          beiBestaetigen={() => aufheben(nachfrage.id)}
          beiSchliessen={() => {
            setzeNachfrage(null);
          }}
        >
          <p>
            {nachfrage.ressourcenbezeichnung} ist für diese Zeit wieder frei
            {nachfrage.terminbezeichnung !== null
              ? ` und fehlt danach bei „${nachfrage.terminbezeichnung}“`
              : ''}
            . Die Zuordnung bleibt als abgesagt im Kalender stehen und lässt sich nicht
            zurücknehmen — für dieselbe Zeit legt man eine neue an.
          </p>
        </Rueckfrage>
      )}
    </>
  );

  /** Die Anlage in der Seitenleiste des Stamms (RES-01). */
  function anlageformular(): JSX.Element {
    return (
      <form onSubmit={anlegenAbsenden}>
        <div className="karte__titel">
          <h2>Neue Ressource</h2>
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="neuKennung">
            Kennung <span className="feld__pflicht">Pflicht</span>
          </label>
          <input
            id="neuKennung"
            className="feld__eingabe"
            value={neuKennung}
            onChange={(e) => {
              setzeNeuKennung(e.target.value);
            }}
            placeholder="M3, SR-1, TR-Meier"
          />
          <p className="feld__hilfe">
            Sie steht auf dem Tagesplan und im Dienstplan. Vergeben, nicht errechnet — eine
            abgeleitete Kennung ändert sich mit den Feldern, aus denen sie folgt.
          </p>
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="neuBezeichnung">
            Bezeichnung <span className="feld__pflicht">Pflicht</span>
          </label>
          <input
            id="neuBezeichnung"
            className="feld__eingabe"
            value={neuBezeichnung}
            onChange={(e) => {
              setzeNeuBezeichnung(e.target.value);
            }}
          />
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="neuArt">
            Art
          </label>
          <select
            id="neuArt"
            className="feld__eingabe"
            value={neuArt}
            onChange={(e) => {
              setzeNeuArt(e.target.value as Ressourcenart);
            }}
          >
            {/*
              NUR DIE ARTEN DIESES KREISES. Wer in „Assets" einen Trainer
              anlegen könnte, legte ihn an — und fände ihn danach nicht
              wieder, weil die Liste ihn nicht zeigt.
            */}
            {arten.map((a) => (
              <option key={a} value={a}>
                {ARTNAME[a]}
              </option>
            ))}
          </select>
        </div>
        {/*
          ÄA-21: Bei einer Person ist die Zugehörigkeit Pflicht — die
          Datenbank verlangt sie (0181). Sie steht deshalb im Formular und
          nicht in einem Nachtrag, und sie erscheint nur dort, wo sie gilt:
          Ein Raum ist weder intern noch extern.
        */}
        {(neuArt === 'trainer' || neuArt === 'dienstleister') && (
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="neuZugehoerigkeit">
              Zugehörigkeit <span className="feld__pflicht">Pflicht</span>
            </label>
            <select
              id="neuZugehoerigkeit"
              className="feld__eingabe"
              value={neuZugehoerigkeit}
              onChange={(e) => {
                setzeNeuZugehoerigkeit(e.target.value as 'intern' | 'extern');
              }}
            >
              {(['extern', 'intern'] as const).map((z) => (
                <option key={z} value={z}>
                  {ZUGEHOERIGKEITSNAME[z] ?? z}
                </option>
              ))}
            </select>
            {/* RES-39 */}
            {/* RES-49 */}
            <span className="feld__hilfe">
              Intern wird in der Trainingsplanung einfach zugeordnet; extern wird
              angefragt und braucht eine Vereinbarung.
            </span>
          </div>
        )}
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="neuStandort">
            Standort
          </label>
          <select
            id="neuStandort"
            className="feld__eingabe"
            value={neuStandort}
            onChange={(e) => {
              setzeNeuStandort(e.target.value);
            }}
          >
            <option value="">ohne Standort — überall einsetzbar</option>
            {standorte.map((o) => (
              <option key={o.kennung} value={o.kennung}>
                {o.bezeichnung}
              </option>
            ))}
          </select>
        </div>
        {/*
          DIE BELEGUNGSART GIBT ES NUR BEI DEN ASSETS (13.09.2026).

          Auftraggeber: „Belegung raus, da immer nur einzeln." Ein Mensch
          steht zu einer Zeit an einer Stelle — „nach Plätzen" ist an ihm
          keine Einstellung, sondern ein Fehler, der sich einstellen ließe.
          Der Wert bleibt `ausschliesslich` und wird mitgeschickt; die
          Spalte ist NOT NULL (0062).
        */}
        {kreis !== 'trainer' && (
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="neuBelegungsart">
              Belegungsart
            </label>
            <select
              id="neuBelegungsart"
              className="feld__eingabe"
              value={neuBelegungsart}
              onChange={(e) => {
                setzeNeuBelegungsart(e.target.value as 'ausschliesslich' | 'plaetze');
              }}
            >
              <option value="ausschliesslich">ausschließlich belegbar</option>
              <option value="plaetze">mit Platzzahl</option>
            </select>
            {/* RES-16 */}
            <p className="feld__hilfe">
              Eine Person oder ein Fahrzeug ist ausschließlich belegbar; ein Raum hat Plätze.
              Davon hängt der Doppelbelegungsschutz ab.
            </p>
          </div>
        )}
        {/*
         * Die Platzzahl erscheint NUR bei der Platzbelegung. Ein Feld,
         * das bei „ausschließlich" dasteht, lädt zum Ausfüllen ein — und
         * die Datenbank weist es dann ab (RES-03).
         */}
        {neuBelegungsart === 'plaetze' && (
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="neuPlatzzahl">
              Platzzahl <span className="feld__pflicht">Pflicht</span>
            </label>
            <input
              id="neuPlatzzahl"
              type="number"
              min={1}
              className="feld__eingabe"
              value={neuPlatzzahl}
              onChange={(e) => {
                setzeNeuPlatzzahl(e.target.value);
              }}
            />
          </div>
        )}
        <div className="knopfreihe knopfreihe--abschluss">
          <button type="submit" className="knopf knopf--haupt" disabled={laedt}>
            Anlegen
          </button>
        </div>
      </form>
    );
  }

  /** Die Zuordnung in der Seitenleiste des Kalenders (RES-18). */
  function zuordnungsformular(): JSX.Element {
    return (
      <form onSubmit={zuordnen}>
        <div className="karte__titel">
          <h2>Ressource zuordnen</h2>
        </div>
        {/* RES-16 */}
        <p className="hilfe">
          Die Zuordnung wird geschrieben und von der Datenbank abgewiesen, wenn sie sich
          stößt — nicht vorher geprüft. Zwei Disponenten, die gleichzeitig fragen, sähen
          beide „frei". Sie gilt für den Tag aus der Filterleiste.
        </p>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="zuRessource">
            Ressource <span className="feld__pflicht">Pflicht</span>
          </label>
          <select
            id="zuRessource"
            className="feld__eingabe"
            value={zuRessource}
            onChange={(e) => {
              setzeZuRessource(e.target.value);
            }}
          >
            <option value="">— bitte wählen —</option>
            {ressourcen
              .filter((r) => r.status === 'aktiv')
              .map((r) => (
                <option key={r.id} value={r.id}>
                  {r.kennung} — {r.bezeichnung}
                </option>
              ))}
          </select>
        </div>
        <div className="feldreihe feldreihe--halb">
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="vonUhr">
              Von
            </label>
            <input
              id="vonUhr"
              type="time"
              className="feld__eingabe"
              value={vonUhr}
              onChange={(e) => {
                setzeVonUhr(e.target.value);
              }}
            />
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bisUhr">
              Bis
            </label>
            <input
              id="bisUhr"
              type="time"
              className="feld__eingabe"
              value={bisUhr}
              onChange={(e) => {
                setzeBisUhr(e.target.value);
              }}
            />
          </div>
        </div>
        {/* RES-06 */}
        <p className="feld__hilfe">
          Der Zeitraum schließt Vor- und Nachbereitung ein: Sie belegen die Ressource,
          verlängern aber nicht den Kundentermin.
        </p>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="zuStatus">
            Art der Zuordnung
          </label>
          <select
            id="zuStatus"
            className="feld__eingabe"
            value={zuStatus}
            onChange={(e) => {
              setzeZuStatus(e.target.value as 'vorlaeufig' | 'bestaetigt');
            }}
          >
            <option value="vorlaeufig">vorläufig</option>
            <option value="bestaetigt">bestätigt</option>
          </select>
          {/* RES-22 */}
          <p className="feld__hilfe">
            Eine vorläufige Zuordnung blockiert die Ressource ebenso — sie ist nur als
            vorläufig erkennbar.
          </p>
        </div>
        {/*
         * Die Platzzahl nur, wenn die gewählte Ressource nach Plätzen
         * belegt wird. Sonst weist die Datenbank sie ab (RES-03), und ein
         * sichtbares Feld hätte dazu eingeladen.
         */}
        {ressourcen.find((r) => r.id === zuRessource)?.belegungsart === 'plaetze' && (
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="zuPlaetze">
              Plätze <span className="feld__pflicht">Pflicht</span>
            </label>
            <input
              id="zuPlaetze"
              type="number"
              min={1}
              className="feld__eingabe"
              value={zuPlaetze}
              onChange={(e) => {
                setzeZuPlaetze(e.target.value);
              }}
            />
          </div>
        )}
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="zuBemerkung">
            Bemerkung
          </label>
          <input
            id="zuBemerkung"
            className="feld__eingabe"
            value={zuBemerkung}
            onChange={(e) => {
              setzeZuBemerkung(e.target.value);
            }}
          />
        </div>
        <div className="knopfreihe knopfreihe--abschluss">
          <button
            type="submit"
            className="knopf knopf--haupt"
            disabled={laedt || zuRessource === ''}
          >
            Zuordnen
          </button>
        </div>
      </form>
    );
  }
}
